import React, { useEffect, useState } from 'react';
import { useT } from '../context/LanguageContext';
import { carsysApi } from '../api/carsysApi';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const debetNatuur = (rubriek) => rubriek === 'Activa' || rubriek === 'Kosten';

export default function PrintBoekhoudRapport({ gegevens, periodeLabel, tot, btw, wv, onClose }) {
  const { t } = useT();
  const accent = gegevens?.bonLayout?.kleur || '#2563eb';
  const [activa, setActiva] = useState([]);
  const [passiva, setPassiva] = useState([]);
  const [resultaat, setResultaat] = useState(0);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Balans-saldi t/m einde periode (cumulatief)
  useEffect(() => {
    Promise.all([carsysApi.getGrootboekRekeningen(), carsysApi.getJournaalposten()])
      .then(([rekeningen, posten]) => {
        const cutoff = tot ? new Date(tot) : null;
        const m = {};
        rekeningen.forEach(r => { m[r.code] = { ...r, debet: 0, credit: 0 }; });
        posten.forEach(j => {
          if (cutoff) { try { if (new Date(j.datum) > cutoff) return; } catch { /* */ } }
          (j.regels || []).forEach(l => {
            if (!m[l.rekening]) m[l.rekening] = { code: l.rekening, naam: l.rekening, rubriek: 'Overig', debet: 0, credit: 0 };
            m[l.rekening].debet += (Number(l.debet) || 0);
            m[l.rekening].credit += (Number(l.credit) || 0);
          });
        });
        const all = Object.values(m).map(a => ({ ...a, saldo: debetNatuur(a.rubriek) ? a.debet - a.credit : a.credit - a.debet }));
        setActiva(all.filter(a => a.rubriek === 'Activa' && Math.abs(a.saldo) > 0.005).sort((a, b) => a.code.localeCompare(b.code)));
        setPassiva(all.filter(a => a.rubriek === 'Passiva' && Math.abs(a.saldo) > 0.005).sort((a, b) => a.code.localeCompare(b.code)));
        const opbr = all.filter(a => a.rubriek === 'Opbrengsten').reduce((s, a) => s + a.saldo, 0);
        const kost = all.filter(a => a.rubriek === 'Kosten').reduce((s, a) => s + a.saldo, 0);
        setResultaat(opbr - kost);
      }).catch(() => {});
  }, [tot]);

  const totaalActiva = activa.reduce((s, a) => s + a.saldo, 0);
  const totaalPassiva = passiva.reduce((s, a) => s + a.saldo, 0) + resultaat;

  const print = () => {
    const node = document.getElementById('boekhoud-print-content');
    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) { alert('Sta pop-ups toe om af te drukken / pdf te maken.'); return; }
    w.document.open();
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Boekhoudrapport ${periodeLabel}</title>
      <style>
        body { font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; color: #0f172a; margin: 0; padding: 18mm; font-size: 12px; }
        h1 { font-size: 20px; color: ${accent}; margin: 0 0 2px; }
        h2 { font-size: 14px; border-bottom: 2px solid ${accent}; padding-bottom: 4px; margin: 22px 0 10px; }
        .rp-head { border-bottom: 3px solid ${accent}; padding-bottom: 12px; margin-bottom: 8px; display:flex; justify-content:space-between; align-items:flex-start; }
        .rp-sub { font-size: 11px; color: #475569; line-height: 1.5; }
        table { width: 100%; border-collapse: collapse; font-size: 11.5px; margin-bottom: 8px; }
        th { background:#f1f5f9; text-align:left; padding:6px 8px; border-bottom:1px solid #cbd5e1; }
        td { padding: 5px 8px; border-bottom: 1px solid #eef2f7; }
        td.r, th.r { text-align: right; }
        tr.tot td { border-top: 2px solid ${accent}; font-weight: 700; border-bottom: none; }
        .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
        .muted { color:#64748b; font-size:10px; margin-top:6px; }
        @page { size: A4; margin: 0; }
      </style></head><body>${node.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch { /* */ } }, 250);
  };

  const Saldo = ({ label, value, color }) => (
    <tr className="tot"><td>{label}</td><td className="r" style={color ? { color } : undefined}>{eur(value)}</td></tr>
  );

  return (
    <div className="print-overlay">
      <div className="print-controls no-print">
        <button className="cs-btn" onClick={onClose} data-testid="rapport-sluiten">{t('Sluiten', 'Sluiten')} (Esc)</button>
        <button className="cs-btn primary" onClick={print} data-testid="rapport-print">{t('Afdrukken / Opslaan als PDF', 'Afdrukken / Opslaan als PDF')}</button>
      </div>

      <div className="print-page" id="boekhoud-print-content">
        <div className="rp-head">
          <div>
            <h1>{gegevens?.naam || 'MonteurCentraal'}</h1>
            <div className="rp-sub">
              {gegevens?.adres}{gegevens?.adres ? <br /> : null}
              {gegevens?.postcode} {gegevens?.plaats}<br />
              {gegevens?.kvk && <>KvK: {gegevens.kvk} · </>}{gegevens?.btw && <>BTW: {gegevens.btw}</>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{t('Boekhoudrapport', 'Boekhoudrapport')}</div>
            <div className="rp-sub">{t('Periode', 'Periode')}: <b>{periodeLabel}</b><br />{t('Opgesteld', 'Opgesteld')}: {new Date().toLocaleDateString('nl-NL')}</div>
          </div>
        </div>

        {/* BTW-aangifte */}
        <h2>{t('BTW-aangifte', 'BTW-aangifte')}</h2>
        <table>
          <thead><tr><th>{t('Tarief', 'Tarief')}</th><th className="r">{t('Omzet (excl.)', 'Omzet (excl.)')}</th><th className="r">{t('BTW', 'BTW')}</th></tr></thead>
          <tbody>
            {Object.entries(btw.perTarief || {}).sort((a, b) => b[0] - a[0]).map(([tar, v]) => (
              <tr key={tar}><td>{tar}%</td><td className="r">{eur(v.netto)}</td><td className="r">{eur(v.btw)}</td></tr>
            ))}
            {Object.keys(btw.perTarief || {}).length === 0 && <tr><td colSpan={3} style={{ color: '#888' }}>{t('Geen omzet in deze periode', 'Geen omzet in deze periode')}</td></tr>}
          </tbody>
        </table>
        <table>
          <tbody>
            <tr><td>{t('Verschuldigde BTW (over omzet)', 'Verschuldigde BTW (over omzet)')}</td><td className="r">{eur(btw.verschuldigd)}</td></tr>
            <tr><td>{t('Voorbelasting (over inkopen)', 'Voorbelasting (over inkopen)')}</td><td className="r">− {eur(btw.voorbelasting)}</td></tr>
            <Saldo label={btw.saldo >= 0 ? t('Te betalen aan Belastingdienst', 'Te betalen aan Belastingdienst') : t('Terug te ontvangen', 'Terug te ontvangen')} value={Math.abs(btw.saldo)} color={btw.saldo >= 0 ? '#b91c1c' : '#15803d'} />
          </tbody>
        </table>

        {/* Winst & Verlies */}
        <h2>{t('Winst & Verlies', 'Winst & Verlies')}</h2>
        <table>
          <tbody>
            <tr><td>{t('Omzet (excl. BTW)', 'Omzet (excl. BTW)')}</td><td className="r">{eur(wv.omzetExcl)}</td></tr>
            <tr><td>{t('Autohandel marge', 'Autohandel marge')}</td><td className="r">{eur(wv.autoMarge)}</td></tr>
            <tr><td><b>{t('Totale opbrengsten', 'Totale opbrengsten')}</b></td><td className="r"><b>{eur((wv.omzetExcl || 0) + (wv.autoMarge || 0))}</b></td></tr>
            {(wv.kostenPerCategorie || []).map(([c, b]) => (
              <tr key={c}><td style={{ paddingLeft: 18, color: '#475569' }}>{c}</td><td className="r" style={{ color: '#475569' }}>− {eur(b)}</td></tr>
            ))}
            <tr><td>{t('Kosten (excl. BTW)', 'Kosten (excl. BTW)')}</td><td className="r">− {eur(wv.kostenExcl)}</td></tr>
            <Saldo label={`${t('Resultaat', 'Resultaat')} (${wv.resultaat >= 0 ? t('winst', 'winst') : t('verlies', 'verlies')})`} value={wv.resultaat} color={wv.resultaat >= 0 ? '#15803d' : '#b91c1c'} />
          </tbody>
        </table>

        {/* Balans */}
        <h2>{t('Balans', 'Balans')} ({t('per', 'per')} {tot ? new Date(tot).toLocaleDateString('nl-NL') : new Date().toLocaleDateString('nl-NL')})</h2>
        <div className="cols">
          <table>
            <thead><tr><th colSpan={2}>{t('Activa (bezittingen)', 'Activa (bezittingen)')}</th></tr></thead>
            <tbody>
              {activa.map(a => <tr key={a.code}><td>{a.code} {a.naam}</td><td className="r">{eur(a.saldo)}</td></tr>)}
              {activa.length === 0 && <tr><td colSpan={2} style={{ color: '#888' }}>{t('Geen activa', 'Geen activa')}</td></tr>}
              <Saldo label={t('Totaal activa', 'Totaal activa')} value={totaalActiva} />
            </tbody>
          </table>
          <table>
            <thead><tr><th colSpan={2}>{t('Passiva (schulden & vermogen)', 'Passiva (schulden & vermogen)')}</th></tr></thead>
            <tbody>
              {passiva.map(a => <tr key={a.code}><td>{a.code} {a.naam}</td><td className="r">{eur(a.saldo)}</td></tr>)}
              <tr><td>{t('Resultaat boekjaar', 'Resultaat boekjaar')}</td><td className="r" style={{ color: resultaat >= 0 ? '#15803d' : '#b91c1c' }}>{eur(resultaat)}</td></tr>
              <Saldo label={t('Totaal passiva', 'Totaal passiva')} value={totaalPassiva} />
            </tbody>
          </table>
        </div>

        <p className="muted">{t('Indicatief overzicht op basis van je facturen, betalingen en kosten. Raadpleeg je boekhouder voor de definitieve aangifte en jaarrekening.', 'Indicatief overzicht op basis van je facturen, betalingen en kosten. Raadpleeg je boekhouder voor de definitieve aangifte en jaarrekening.')}</p>
      </div>

      <style>{`
        .print-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.85); z-index: 200; overflow: auto; padding: 30px; }
        .print-controls { position: fixed; top: 20px; right: 30px; display: flex; gap: 8px; z-index: 210; }
        .print-page { background: #fff; max-width: 800px; margin: 0 auto; padding: 48px 52px; color: #0f172a; box-shadow: 0 8px 40px rgba(0,0,0,0.4); }
        .print-page h1 { font-size: 22px; color: ${accent}; margin: 0 0 2px; }
        .print-page h2 { font-size: 15px; border-bottom: 2px solid ${accent}; padding-bottom: 5px; margin: 22px 0 10px; }
        .print-page .rp-head { border-bottom: 3px solid ${accent}; padding-bottom: 12px; margin-bottom: 8px; display:flex; justify-content:space-between; align-items:flex-start; }
        .print-page .rp-sub { font-size: 11.5px; color: #475569; line-height: 1.5; }
        .print-page table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px; }
        .print-page th { background:#f1f5f9; text-align:left; padding:6px 8px; border-bottom:1px solid #cbd5e1; }
        .print-page td { padding: 5px 8px; border-bottom: 1px solid #eef2f7; }
        .print-page td.r, .print-page th.r { text-align: right; }
        .print-page tr.tot td { border-top: 2px solid ${accent}; font-weight: 700; border-bottom: none; }
        .print-page .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
        .print-page .muted { color:#64748b; font-size:10.5px; margin-top:14px; }
        @media print {
          body * { visibility: hidden; }
          .print-overlay, .print-overlay * { visibility: visible; }
          .print-overlay { position: static; padding: 0; background: white; }
          .no-print { display: none !important; }
          .print-page { box-shadow: none; max-width: none; padding: 18mm; }
          @page { size: A4; margin: 0; }
        }
      `}</style>
    </div>
  );
}
